"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.BatchEvalEvaluatorMappingSchema = exports.observationVariableMappingList = exports.observationVariableMapping = exports.TimeScopeSchema = exports.JobTimeScopeZod = exports.DEFAULT_TRACE_JOB_DELAY = exports.availableDatasetEvalVariables = exports.availableTraceEvalVariables = exports.wipVariableMapping = exports.variableMappingList = exports.variableMapping = exports.langfuseObjects = exports.BatchEvalSourceTableSchema = exports.BatchEvalSourceTable = exports.EvalTargetObjectSchema = exports.EvalTargetObject = exports.EvaluatorPromptMessagesSchema = exports.EvaluatorPromptMessageSchema = exports.EvaluatorPromptMessageRoleSchema = exports.EvalTemplateSourceCodeLanguageEnum = exports.EvalTemplateTypeEnum = void 0;
exports.getEvaluatorPromptMessages = getEvaluatorPromptMessages;
exports.getEvalTargetObjectFromSourceTable = getEvalTargetObjectFromSourceTable;
const zod_1 = __importDefault(require("zod"));
/**
 * Client-safe mirrors of the Prisma enums. The barrel only reaches the Prisma
 * values through `export * from "@prisma/client"`, which bundlers that resolve
 * this package from source (Vite/Storybook) cannot turn into named ESM exports,
 * so browser code must use these instead of importing the Prisma values. The
 * `satisfies` keeps them exhaustive: a new schema member fails the build here.
 */
exports.EvalTemplateTypeEnum = {
    LLM_AS_JUDGE: "LLM_AS_JUDGE",
    CODE: "CODE",
    DECISION_MODEL: "DECISION_MODEL",
    FACET: "FACET",
};
exports.EvalTemplateSourceCodeLanguageEnum = {
    PYTHON: "PYTHON",
    TYPESCRIPT: "TYPESCRIPT",
};
exports.EvaluatorPromptMessageRoleSchema = zod_1.default.enum([
    "system",
    "user",
    "assistant",
]);
exports.EvaluatorPromptMessageSchema = zod_1.default
    .object({
    role: exports.EvaluatorPromptMessageRoleSchema,
    content: zod_1.default.string().refine((content) => content.trim().length > 0, {
        message: "Prompt messages cannot be empty",
    }),
})
    .strict();
exports.EvaluatorPromptMessagesSchema = zod_1.default
    .array(exports.EvaluatorPromptMessageSchema)
    .min(1)
    .refine((messages) => !messages.some((message, index) => index > 0 && message.role === "system"), {
    message: "System messages are only allowed as the first prompt message",
});
const LEGACY_EMPTY_PROMPT_PLACEHOLDER = "No prompt provided";
function getEvaluatorPromptMessages(params) {
    const parsed = exports.EvaluatorPromptMessagesSchema.safeParse(params.promptMessages);
    if (parsed.success)
        return parsed.data;
    // Historical evaluator rows may contain blank prompts that do not satisfy
    // the current message schema. Keep those rows readable with valid content.
    const legacyPrompt = params.prompt?.trim()
        ? params.prompt
        : LEGACY_EMPTY_PROMPT_PLACEHOLDER;
    return [{ role: "user", content: legacyPrompt }];
}
exports.EvalTargetObject = {
    TRACE: "trace",
    DATASET: "dataset",
    EVENT: "event",
    EXPERIMENT: "experiment",
};
exports.EvalTargetObjectSchema = zod_1.default.enum(Object.values(exports.EvalTargetObject));
// Batch action source tables that support evaluation
exports.BatchEvalSourceTable = {
    EVENTS: "events",
    EXPERIMENT_ITEMS: "experiment-items",
    EXPERIMENTS: "experiments",
};
exports.BatchEvalSourceTableSchema = zod_1.default.enum([
    exports.BatchEvalSourceTable.EVENTS,
    exports.BatchEvalSourceTable.EXPERIMENT_ITEMS,
    exports.BatchEvalSourceTable.EXPERIMENTS,
]);
/**
 * Maps a batch evaluation source table to its corresponding eval target object.
 * - "events" → EvalTargetObject.EVENT (observation-scoped evaluators)
 * - "experiment-items" / "experiments" → EvalTargetObject.EXPERIMENT (experiment-scoped evaluators)
 */
function getEvalTargetObjectFromSourceTable(sourceTable) {
    return sourceTable === exports.BatchEvalSourceTable.EVENTS
        ? exports.EvalTargetObject.EVENT
        : exports.EvalTargetObject.EXPERIMENT;
}
exports.langfuseObjects = [
    "trace",
    "span",
    "generation",
    "event",
    "agent",
    "tool",
    "chain",
    "retriever",
    "evaluator",
    "embedding",
    "guardrail",
    "dataset_item",
];
const langfuseObject = zod_1.default.enum(exports.langfuseObjects);
// variable mapping stored in the db for eval templates
exports.variableMapping = zod_1.default
    .object({
    templateVariable: zod_1.default.string(), // variable name in the template
    // name of the observation to extract the variable from
    // not required for trace or dataset_item, as we only have one of each.
    objectName: zod_1.default.string().nullish(),
    langfuseObject: langfuseObject,
    selectedColumnId: zod_1.default.string(),
    jsonSelector: zod_1.default.string().nullish(),
})
    .refine((value) => value.langfuseObject === "trace" ||
    value.langfuseObject === "dataset_item" ||
    value.objectName !== null, {
    message: "objectName is required for observation objects (generation, span, score)",
});
exports.variableMappingList = zod_1.default.array(exports.variableMapping);
// WIP version for forms - langfuseObject optional to support both:
// - Trace/Dataset evals: Include langfuseObject and objectName to specify which observation
// - Event/Experiment evals: Omit them since the observation is already selected
exports.wipVariableMapping = zod_1.default.object({
    templateVariable: zod_1.default.string(),
    objectName: zod_1.default.string().nullish(),
    langfuseObject: langfuseObject.optional(),
    selectedColumnId: zod_1.default.string().nullish(),
    jsonSelector: zod_1.default.string().nullish(),
});
const observationCols = [
    {
        name: "Metadata",
        id: "metadata",
        type: "stringObject",
        internal: 'o."metadata"',
    },
    { name: "Input", id: "input", internal: 'o."input"' },
    { name: "Output", id: "output", internal: 'o."output"' },
];
exports.availableTraceEvalVariables = [
    {
        id: "agent",
        display: "Agent",
        availableColumns: observationCols,
    },
    {
        id: "chain",
        display: "Chain",
        availableColumns: observationCols,
    },
    {
        id: "embedding",
        display: "Embedding",
        availableColumns: observationCols,
    },
    {
        id: "evaluator",
        display: "Evaluator",
        availableColumns: observationCols,
    },
    {
        id: "event",
        display: "Event",
        availableColumns: observationCols,
    },
    {
        id: "generation",
        display: "Generation",
        availableColumns: observationCols,
    },
    {
        id: "guardrail",
        display: "Guardrail",
        availableColumns: observationCols,
    },
    {
        id: "retriever",
        display: "Retriever",
        availableColumns: observationCols,
    },
    {
        id: "span",
        display: "Span",
        availableColumns: observationCols,
    },
    {
        id: "tool",
        display: "Tool",
        availableColumns: observationCols,
    },
    {
        id: "trace",
        display: "Trace",
        availableColumns: [
            {
                name: "Metadata",
                id: "metadata",
                type: "stringObject",
                internal: 't."metadata"',
            },
            { name: "Input", id: "input", internal: 't."input"' },
            { name: "Output", id: "output", internal: 't."output"' },
        ],
    },
];
exports.availableDatasetEvalVariables = [
    {
        id: "dataset_item",
        display: "Dataset item",
        availableColumns: [
            {
                name: "Metadata",
                id: "metadata",
                type: "stringObject",
                internal: 'd."metadata"',
            },
            { name: "Input", id: "input", internal: 'd."input"' },
            {
                name: "Expected output",
                id: "expected_output",
                internal: 'd."expected_output"',
            },
        ],
    },
    ...exports.availableTraceEvalVariables,
];
exports.DEFAULT_TRACE_JOB_DELAY = 10_000;
exports.JobTimeScopeZod = zod_1.default.enum(["NEW", "EXISTING"]);
exports.TimeScopeSchema = zod_1.default.array(exports.JobTimeScopeZod).default(["NEW"]);
// Simplified variable mapping for observation-based evals.
// Unlike trace-based evals, we don't need objectName since we're directly
// targeting a specific observation - no need to specify which observation to extract from.
exports.observationVariableMapping = zod_1.default.object({
    templateVariable: zod_1.default.string(), // variable name in the template
    selectedColumnId: zod_1.default.string(), // column to extract (must match observationEvalVariableColumns.id)
    jsonSelector: zod_1.default.string().nullish(), // optional JSON path selector
});
exports.observationVariableMappingList = zod_1.default.array(exports.observationVariableMapping);
/**
 * Per-evaluator mapping override for a one-shot batch evaluation. `null`
 * inherits the evaluator version's mapping. Absent from the payload means
 * every selected evaluator inherits.
 */
exports.BatchEvalEvaluatorMappingSchema = zod_1.default.object({
    evaluatorId: zod_1.default.string().min(1),
    variableMapping: exports.observationVariableMappingList.nullable(),
});
